import { forwardRef, useState } from "react";

import {
  ChevronDown,
  ChevronUp,
} from "@hl/base-components/lib/assets/icons/HDS/";
import useMantineMediaQueries from "@hl/base-components/lib/hooks/useMantineMediaQueries";
import {
  Box,
  Flex,
  Group,
  Select,
  Stack,
  Text,
  UnstyledButton,
} from "@mantine/core";
import { ethers } from "ethers";

import {
  Contract2,
  PriceType,
  TokenDistributionType,
  _CollectionType,
} from "apollo/graphql.generated";

import CollectionDetailsForm from "../CreateCollectionMultistepForm/CollectionDetailsForm";
import CollectionPriceForm from "../CreateCollectionMultistepForm/CollectionPriceForm";
import FormTitle from "../CreateCollectionMultistepForm/FormTitle";
import ReviewAndDeploy from "../CreateCollectionMultistepForm/ReviewAndDeploy";
import SelectSingleImageForm from "../CreateCollectionMultistepForm/SelectImageAndMetadataForm";
import {
  useCreateCollectionFormContext,
  useCreateCollectionState,
} from "../CreateCollectionMultistepForm/context";

/**
 *
 * NON GEN STEPS
 * UploadCollectionAndMetadata / SelectImageAndMetadataForm
 *
 * CollectionDetailsForm
 * CollectionPriceForm
 * ReviewAndDeploy
 *
 * GEN STEPS
 * CodeGenZipUpload
 * CodeGenTestScript
 * SizeAndOutputForm
 * CodeGePreviewImage
 *
 * CollectionDetailsForm
 * CollectionPriceForm
 * ReviewAndDeploy
 */

type Props = {
  contracts?: Contract2[];
};
const QuickDropForm = (props: Props) => {
  const { contracts } = props;
  const { dispatch } = useCreateCollectionState();
  const { isMedium, isLarge } = useMantineMediaQueries();
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);

  const form = useCreateCollectionFormContext();

  const onHandleValidate = () => {
    const result = form.validate();

    if (result.hasErrors) {
      setTimeout(() => {
        const firstError = document.querySelector('[aria-invalid="true"]');
        if (firstError) {
          window.scrollTo({
            top: window.scrollY + firstError.getBoundingClientRect().top - 200,
            behavior: "smooth",
          });
        }
      }, 350);

      return false;
    }

    // Triggers validation
    dispatch({
      type: "JUMP_TO_STEP",
      payload: {
        currentStep: 1,
        collectionType: form.values.collectionType,
        form,
      },
    });

    return true;
  };

  // When selecting the edition type reset some fields
  const onChangeEditionType = (type: _CollectionType) => {
    // Change it here, since BE fails if we do a One of One with size 0
    form.setFieldValue(
      "collection.size",
      type === _CollectionType.OneOfOne ? 1 : 0
    );
    // Reset distribution

    form.setFieldValue("distribution", {
      type: TokenDistributionType.Mint,
      price: 0,
      currency: ethers.constants.AddressZero,
      saleDateFrom: new Date(),
      saleDateTo: null,
      priceType: PriceType.Fixed,
      scheduledPrices: undefined,
      priceDropInterval: undefined,
      isDirectMint: true,
      isFreePrice: true,
    });

    form.getInputProps("collectionType").onChange(type);
  };

  //@ts-ignore
  window.form = form;

  return (
    <Box>
      <FormTitle title={"Edition"} buttons={null} />
      <Flex gap={40} direction={{ base: "column", md: "row-reverse" }}>
        <Box
          sx={{
            width: isLarge ? 560 : isMedium ? 360 : "100%",
          }}
        >
          <SelectSingleImageForm asQuickDropFlow />
        </Box>
        <Stack
          spacing={40}
          sx={{
            minWidth: 0,
            flex: 1,
          }}
        >
          <Select
            label="Edition type"
            {...form.getInputProps("collectionType")}
            onChange={onChangeEditionType}
            itemComponent={EditionTypeSelectItem}
            placeholder="Pick one"
            data={[
              {
                value: _CollectionType.OpenEdition,
                label: "Open",
                description:
                  "An open-ended collection of NFTs in which the artwork and metadata for each token are the same.",
              },
              {
                value: _CollectionType.LimitedEdition,
                label: "Limited",
                description:
                  "A limited collection of NFTs in which the artwork and metadata for each token are the same.",
              },
              {
                value: _CollectionType.OneOfOne,
                label: "One of one",
                description:
                  "A collection containing a single, unique NFT with its own artwork and metadata.",
              },
            ]}
          />
          {/* <SingleBlockchainSelect /> */}
          <CollectionDetailsForm contracts={contracts ?? []} asQuickDropFlow />
          <CollectionPriceForm contracts={contracts ?? []} asQuickDropFlow />

          <UnstyledButton
            onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          >
            <Group noWrap position="apart">
              <Stack spacing={0}>
                <Text size="sm" fw={500}>
                  View advanced options
                </Text>
                <Text size="sm" c={"dimmed"}>
                  You can add a gate, add traits, edit payout address, edit
                  royalties,
                  {form.values.collectionType === _CollectionType.LimitedEdition
                    ? ""
                    : " choose your contract"}{" "}
                  and more
                </Text>
              </Stack>
              <Box>{showAdvancedOptions ? <ChevronUp /> : <ChevronDown />}</Box>
            </Group>
          </UnstyledButton>

          <Stack
            id="advanced-options-portal-target"
            sx={{
              display: showAdvancedOptions ? "flex" : "none",
              ".optional-portal": {
                display: "flex",
                flexDirection: "column",
                gap: "40px",
              },
            }}
            spacing={40}
          ></Stack>
          <ReviewAndDeploy
            contracts={contracts}
            handleValidate={onHandleValidate}
            handleSubmit={() => {
              dispatch({
                type: "DEPLOY_COLLECTION",
              });
            }}
            buttonOnly
          />
          {/* <pre>{JSON.stringify(form.errors, null, 2)}</pre> */}
        </Stack>
      </Flex>
    </Box>
  );
};

export default QuickDropForm;

// Components ========================================

type EditionTypeSelectItemProps = {
  label: string;
  description: string;
};
const EditionTypeSelectItem = forwardRef<
  HTMLDivElement,
  EditionTypeSelectItemProps
>(({ label, description, ...others }: EditionTypeSelectItemProps, ref) => (
  <div ref={ref} {...others}>
    <Stack spacing={4}>
      <Text size={"sm"}>{label}</Text>
      <Text size={"xs"} c={"dimmed"}>
        {description}
      </Text>
    </Stack>
  </div>
));
